// /academy/estrategias — Library of pro trading strategies.
// Card grid with type + difficulty filters; each card links to the detail
// page at /academy/estrategias/:id.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import {
  getEstrategias,
  getEstrategiaTipos,
  getEstrategiaNiveles,
  TIPO_COLORS,
  NIVEL_COLORS,
  labelTipoEstrategia,
  labelNivelEstrategia,
} from "../data/i18n-academy";

const AcademyEstrategiasPage = () => {
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const ESTRATEGIAS = getEstrategias(lang);
  const ESTRATEGIA_TIPOS = getEstrategiaTipos(lang);
  const ESTRATEGIA_NIVELES = getEstrategiaNiveles(lang);
  const [tipo, setTipo] = useState("todas");
  const [nivel, setNivel] = useState("todas");

  const filtered = useMemo(
    () =>
      ESTRATEGIAS.filter(
        (e) =>
          (tipo === "todas" || e.tipo === tipo) &&
          (nivel === "todas" || e.dificultad === nivel)
      ),
    [tipo, nivel, ESTRATEGIAS]
  );

  return (
    <AcademyShell
      testid="academy-estrategias"
      eyebrow={T.estr_eyebrow}
      eyebrowIcon={FlaskConical}
      title={T.estr_title}
      subtitle={T.estr_subtitle}
      breadcrumbCurrent={T.estr_breadcrumb}
      footHint={`${ESTRATEGIAS.length} ${T.estr_foot}`}
    >
      <FilterRow label={T.estr_filter_type}>
        {ESTRATEGIA_TIPOS.map((f) => (
          <Pill
            key={f.id}
            active={tipo === f.id}
            onClick={() => setTipo(f.id)}
            testid={`estr-tipo-${f.id}`}
          >
            {T.estr_types[f.id] || f.label}
          </Pill>
        ))}
      </FilterRow>

      <FilterRow label={T.estr_filter_lvl}>
        {ESTRATEGIA_NIVELES.map((f) => (
          <Pill
            key={f.id}
            active={nivel === f.id}
            onClick={() => setNivel(f.id)}
            testid={`estr-nivel-${f.id}`}
          >
            {T.estr_levels[f.id] || f.label}
          </Pill>
        ))}
      </FilterRow>

      <p className="mb-4 mt-2 text-xs text-white/40">
        <span className="font-bold text-orange-300">{filtered.length}</span> / {ESTRATEGIAS.length}
      </p>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="estr-grid">
        {filtered.map((e) => (
          <Link
            key={e.id}
            to={`/academy/estrategias/${e.id}`}
            data-testid={`estr-card-${e.id}`}
            className="group flex flex-col rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left transition-all hover:border-orange-400/40 hover:bg-orange-400/[0.04]"
          >
            <div className="mb-2 flex flex-wrap gap-1.5">
              <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] ${TIPO_COLORS[e.tipo]}`}>
                {labelTipoEstrategia(e.tipo, lang)}
              </span>
              <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] ${NIVEL_COLORS[e.dificultad]}`}>
                {labelNivelEstrategia(e.dificultad, lang)}
              </span>
            </div>
            <h3 className="font-display text-lg font-black leading-tight text-white">{e.nombre}</h3>
            <p className="mt-2 line-clamp-3 text-[12px] text-white/55">{e.descripcion}</p>

            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-white/5 pt-3 font-mono text-[10px]">
              <Stat k={T.estr_winrate} v={e.winRate} />
              <Stat k={T.estr_rr} v={e.ratioRR} />
              <Stat k={T.estr_markets} v={e.mercados.join(", ")} />
              <Stat k={T.estr_tf} v={e.timeframes.join(", ")} />
            </div>

            <span className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-300 opacity-0 transition-opacity group-hover:opacity-100">
              {T.estr_view}
            </span>
          </Link>
        ))}
      </div>
    </AcademyShell>
  );
};

const FilterRow = ({ label, children }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2">
    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">{label}</span>
    {children}
  </div>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all ${
      active
        ? "border-orange-400/60 bg-orange-500/15 text-orange-200 shadow-[0_0_20px_rgba(255,140,0,0.3)]"
        : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const Stat = ({ k, v }) => (
  <div>
    <div className="text-white/35">{k}</div>
    <div className="font-bold text-white/75">{v}</div>
  </div>
);

export default AcademyEstrategiasPage;
